import { Component, OnInit, Input, SimpleChange } from '@angular/core';
import {
    SmallCardData,
    IBottomCardData,
    IRowItems,
    ITableTdItems,
} from '../../interfaces/card-data';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { NoDataTypes, NoDataSourceType, NoDataActionType } from '../../interfaces/no-data';
import { UserAccessService } from '../../services/user-access.service';
import { CustomDashboardsService } from '../../../my-limegear/services/my-limegear.service';
import { MatDialog } from '@angular/material/dialog';
import { FinancialsAagDialogComponent } from '../financials-aag-dialog/financials-aag-dialog.component';
import { IncentivesService } from 'app/modules/incentives/services/incentives.service';
import { NavigationHelpService } from 'app/modules/shared/services/navigation-help.service';
import { ICardControllerInfo } from 'app/modules/shared/components/card-controller-panel/card-controller-panel.component';

@Component({
    selector: 'small-card',
    templateUrl: './small-card.component.html',
    styleUrls: ['./small-card.component.scss'],
})
export class SmallCardComponent implements OnInit {
    @Input() smallCardData: SmallCardData;
    // TODO: pass these inputs within the smallCardData
    @Input() mainValueFontSize: number = 27;
    @Input() hasBPS?: boolean = false;
    @Input() hasDialog?: boolean = false;
    @Input() isUnclickable?: boolean = false;
    title: string;
    subTitle?: string;
    mainValue: string | number;
    fullValue?: number;
    datePeriod?: string;
    compareTo?: string;
    hFilterJSON?: string;
    unit: string;
    faIcon: string;
    faIconColor: string;
    comparisonValue: string;
    svgData: SafeHtml | string;
    bottomCardData: IBottomCardData[];
    bottomTable: IRowItems[];
    titleFontSize: number = 20;
    isDataReady: boolean;
    hasData: boolean;
    noDataType: NoDataTypes;
    pointerClass: string;
    showPointer: boolean;
    hasAccess: boolean;
    smallCardContainerStyle: object = {};
    cardId: string;
    cardControllerInfo: ICardControllerInfo;

    constructor(
        private sanitizer: DomSanitizer,
        private router: Router,
        private userAccessService: UserAccessService,
        private customDashboardsService: CustomDashboardsService,
        private matDialog: MatDialog,
        private incentivesService: IncentivesService,
        private navigationHelpService: NavigationHelpService
    ) {}

    ngOnInit() {
        if (this.mainValueFontSize != 27) {
            this.titleFontSize = 18;
        }
    }

    ngOnChanges(changes: { [property: string]: SimpleChange }) {
        let change: SimpleChange = changes['smallCardData'];
        if (change) {
            this.setData(change.currentValue);
        }
    }

    setData(data: SmallCardData) {
        this.isDataReady = false;
        this.hasAccess = true;
        this.checkAccessibility(data.routerLink + data.urlSegment);
        this.bottomCardData = [];
        this.bottomTable = [];
        this.noDataType = {
            action: data.action,
            searchTerm: data.searchTerm,
            componentTitle: data.title,
            inputSource: data.noDataSource ? data.noDataSource : NoDataSourceType.smallDataCard,
        };
        if (data.noDataTitleCustomMessage)
            this.noDataType.noDataTitleCustomMessage = data.noDataTitleCustomMessage;
        this.title = data.title;
        this.cardId = data.cardId ?? data.urlSegment;

        if (this.hasAccess) {
            if (data.subTitle) {
                this.subTitle = data.subTitle;
            }
            if (this.hasDialog) {
                this.fullValue = data.fullValue;
                this.datePeriod = data.datePeriod;
                this.compareTo = data.compareTo;
                this.hFilterJSON = data.hFilterJSON;
            }
            this.mainValue = data.mainValue;
            this.unit = data.unit;
            this.faIcon = data.faIcon;
            this.faIconColor = data.faIconColor;
            this.comparisonValue = data.comparisonValue;
            this.svgData = this.sanitizer.bypassSecurityTrustHtml(data.svgData);
            this.bottomCardData = data.bottomCardData ? data.bottomCardData : [];
            this.bottomTable = data.tableData ? data.tableData : [];
            this.isDataReady = data.isDataReady == null ? true : data.isDataReady;
            this.hasData = data.hasData == null ? true : data.hasData;
            this.showPointer = data.hidePointer ? !data.hidePointer : true;

            let showCardControllers =
                (this.customDashboardsService.hasEditPermission() && data.showCardControllers) ??
                false;

            this.cardControllerInfo = {
                cardId: this.cardId,
                showCardControllers: showCardControllers,
                cardTitle: this.title,
            };

            if (this.hasData && this.showPointer && this.hasAccess) {
                this.pointerClass = 'small-card-cursor-pointer';
            } else {
                this.pointerClass = 'small-card-cursor-no-pointer';
            }

            if (data.styleSet) {
                let cardContainerStyle = data.styleSet['card-container-area'];
                this.smallCardContainerStyle = cardContainerStyle ? cardContainerStyle : {};
            }
        } else {
            this.noDataType.action = NoDataActionType.lock;
            this.isDataReady = true;
            this.hasData = false;
        }
    }

    checkAccessibility(url: string) {
        let hasAccess = false;

        if (this.userAccessService.hasAccessToPage(url)) {
            hasAccess = true;
        }
        return hasAccess;
    }

    clickCard() {
        if (this.isUnclickable) {
            return;
        }

        if (this.hasAccess && this.showPointer && this.hasData && !this.hasDialog) {
            this.navigationHelpService.navigateTo(
                [this.smallCardData.routerLink, this.smallCardData.urlSegment],
                this.smallCardData.queryParams,
                true
            );
        } else if (this.hasData) {
            this.openSmallCardDialog();
        }
    }

    clickBottomCardItem(bottomCard: IBottomCardData) {
        if (bottomCard) {
            if (bottomCard.hasAccess && this.showPointer) {
                this.navigationHelpService.navigateTo(
                    [bottomCard.routerLink, bottomCard.urlSegment],
                    bottomCard.queryParams,
                    true
                );
            }
        }
    }

    clickTableCardItem(tdItem: ITableTdItems) {
        if (tdItem) {
            if (tdItem.hasAccess && this.showPointer) {
                this.navigationHelpService.navigateTo(
                    [tdItem.routerLink, tdItem.urlSegment],
                    tdItem.queryParams,
                    true
                );
            }
        }
    }

    openSmallCardDialog() {
        /*
            TODO: Remove these hard coded values and pass values as parameters. 
            These values and parameters can be passed as a configuration value withing the 
            input smallCardData.
            Possibly move all the logic to a service and pass the parameters. 
            Get the dialog name from the parent component within 'smallCardData' and,
            move all the dialog logic to the dialog itself.
            In that way we can support different type of dialogs here. 
        */
        const displayDates = document
            .querySelector('.display-summary')
            .textContent.replace('Date Range: ', '');
        const loanAccount = this.title.includes('LOAN') ? 1 : 2;
        const isVolumeFunded = this.title === 'VOLUME FUNDED';
        const headerInfo = {
            title: this.title,
            value: this.fullValue,
            bps: this.bottomCardData[0].value,
            isSingleMonth: false,
            isVolumeFunded,
            fromDatePeriod: this.datePeriod,
            displayDates,
        };
        const parameters = isVolumeFunded
            ? {
                  fromDatePeriod: this.datePeriod,
                  priorCompareTo: this.compareTo,
                  hFilterJSON: this.hFilterJSON,
              }
            : {
                  fromDatePeriod: this.datePeriod,
                  ymc: null,
                  loanAccount,
                  hFilterJSON: this.hFilterJSON,
              };

        this.incentivesService.getGLAAIncomeVolumeData(parameters, isVolumeFunded).subscribe(
            (data) => {
                const dialogRef = this.matDialog.open(FinancialsAagDialogComponent, {
                    data: { headerData: headerInfo, dialogData: data },
                    panelClass: 'financials-aag-dialog',
                });
            },
            (error) => {
                console.log('error: ', error);
            }
        );
    }
}
